import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faServer,
  faNetworkWired,
  faShieldHalved,
  faBell,
  faMagnifyingGlass,
  faFingerprint,
} from "@fortawesome/free-solid-svg-icons";

export default function HeroVisual() {
  const [hoveredNode, setHoveredNode] = useState(null);

  return (
    <div
      className="cyber-card"
      style={{
        background: "linear-gradient(180deg, #0E1116 0%, #080A0D 100%)",
        border: "1px solid rgba(56, 189, 248, 0.2)",
        boxShadow: "0 20px 40px -15px rgba(0, 0, 0, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.05)",
      }}
    >
      {/* Visual Header / Terminal styling */}
      <div
        className="d-flex align-items-center justify-content-between pb-3 mb-4"
        style={{ borderBottom: "1px solid var(--border-subtle)" }}
      >
        <div className="d-flex align-items-center gap-2">
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#EF4444", opacity: 0.8 }} />
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#F59E0B", opacity: 0.8 }} />
          <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#22C55E", opacity: 0.8 }} />
          <span className="font-mono text-muted ms-2" style={{ fontSize: "0.75rem" }}>
            secops.pipeline.diagram
          </span>
        </div>
        <span
          className="font-mono"
          style={{
            fontSize: "0.7rem",
            color: "var(--accent)",
            background: "rgba(56, 189, 248, 0.08)",
            padding: "3px 8px",
            borderRadius: "4px",
            border: "1px solid rgba(56, 189, 248, 0.2)",
          }}
        >
          TELEMETRY PIPELINE
        </span>
      </div>

      {/* Architecture Flow */}
      <div className="d-flex flex-column align-items-center gap-2">
        {/* Node 1: Endpoints */}
        <div
          className={`w-100 arch-node ${hoveredNode === "endpoints" ? "arch-node-active" : ""}`}
          onMouseEnter={() => setHoveredNode("endpoints")}
          onMouseLeave={() => setHoveredNode(null)}
          style={{ cursor: "pointer" }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: "8px",
                  background: "rgba(56, 189, 248, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--accent)",
                }}
              >
                <FontAwesomeIcon icon={faServer} />
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>ENDPOINTS &amp; HOSTS</div>
                <div style={{ fontSize: "0.78rem", color: "var(--text-secondary)" }}>
                  Windows, Linux, Servers &amp; Firewalls
                </div>
              </div>
            </div>
            <span className="cyber-tag font-mono">Telemetry</span>
          </div>
        </div>

        {/* Connector 1 */}
        <div className="d-flex flex-column align-items-center py-1">
          <div style={{ width: "1px", height: "18px", background: "var(--border-accent)" }} />
          <span className="font-mono text-muted" style={{ fontSize: "0.65rem", padding: "1px 6px" }}>
            TLS 1.3 / Syslog
          </span>
          <div
            style={{
              width: 0,
              height: 0,
              borderLeft: "4px solid transparent",
              borderRight: "4px solid transparent",
              borderTop: "5px solid var(--accent)",
            }}
          />
        </div>

        {/* Node 2: Log Collection */}
        <div
          className={`w-100 arch-node ${hoveredNode === "collection" ? "arch-node-active" : ""}`}
          onMouseEnter={() => setHoveredNode("collection")}
          onMouseLeave={() => setHoveredNode(null)}
          style={{ cursor: "pointer" }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: "8px",
                  background: "rgba(129, 140, 248, 0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#818CF8",
                }}
              >
                <FontAwesomeIcon icon={faNetworkWired} />
              </div>
              <div>
                <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>LOG COLLECTION &amp; AGGREGATION</div>
                <div style={{ fontSize: "0.78rem", color: "var(--text-secondary)" }}>
                  Secure Ingestion, Normalization &amp; Forwarding
                </div>
              </div>
            </div>
            <span className="cyber-tag font-mono">Transport</span>
          </div>
        </div>

        {/* Connector 2 */}
        <div className="d-flex flex-column align-items-center py-1">
          <div style={{ width: "1px", height: "18px", background: "var(--border-accent)" }} />
          <span className="font-mono text-muted" style={{ fontSize: "0.65rem", padding: "1px 6px" }}>
            Event Stream
          </span>
          <div
            style={{
              width: 0,
              height: 0,
              borderLeft: "4px solid transparent",
              borderRight: "4px solid transparent",
              borderTop: "5px solid var(--accent)",
            }}
          />
        </div>

        {/* Node 3: Wazuh SIEM */}
        <div
          className={`w-100 arch-node ${hoveredNode === "wazuh" ? "arch-node-active" : ""}`}
          onMouseEnter={() => setHoveredNode("wazuh")}
          onMouseLeave={() => setHoveredNode(null)}
          style={{
            cursor: "pointer",
            background: "linear-gradient(135deg, rgba(56, 189, 248, 0.08), rgba(14, 17, 22, 0.95))",
            border: "1px solid rgba(56, 189, 248, 0.4)",
          }}
        >
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-3">
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: "8px",
                  background: "rgba(56, 189, 248, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--accent)",
                }}
              >
                <FontAwesomeIcon icon={faShieldHalved} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: "0.98rem", color: "var(--text-primary)" }}>
                  WAZUH SIEM ENGINE
                </div>
                <div style={{ fontSize: "0.78rem", color: "var(--text-secondary)" }}>
                  Decoders, Rule Sets &amp; Correlation Engine
                </div>
              </div>
            </div>
            <span
              className="font-mono"
              style={{
                fontSize: "0.7rem",
                color: "#22C55E",
                background: "rgba(34, 197, 94, 0.1)",
                padding: "2px 8px",
                borderRadius: "4px",
                border: "1px solid rgba(34, 197, 94, 0.3)",
              }}
            >
              Analysis
            </span>
          </div>
        </div>

        {/* Triple Branch Connector */}
        <div className="w-100 position-relative my-2">
          <div
            style={{
              width: "1px",
              height: "12px",
              background: "var(--border-accent)",
              margin: "0 auto",
            }}
          />
          <div
            style={{
              height: "1px",
              background: "var(--border-accent)",
              width: "80%",
              margin: "0 auto",
            }}
          />
          <div className="d-flex justify-content-between" style={{ width: "80%", margin: "0 auto" }}>
            <div style={{ width: "1px", height: "10px", background: "var(--border-accent)" }} />
            <div style={{ width: "1px", height: "10px", background: "var(--border-accent)" }} />
            <div style={{ width: "1px", height: "10px", background: "var(--border-accent)" }} />
          </div>
        </div>

        {/* 3 Outputs: Detection, Alerts, Investigation */}
        <div className="row g-2 w-100">
          <div className="col-4">
            <div
              className="cyber-card-elevated text-center p-2 h-100"
              style={{ border: "1px solid rgba(56, 189, 248, 0.2)" }}
            >
              <FontAwesomeIcon
                icon={faFingerprint}
                style={{ color: "var(--accent)", fontSize: "1rem", marginBottom: "4px" }}
              />
              <div style={{ fontWeight: 600, fontSize: "0.8rem" }}>Detection</div>
              <div className="text-muted font-mono" style={{ fontSize: "0.68rem" }}>
                Rule Match
              </div>
            </div>
          </div>
          <div className="col-4">
            <div
              className="cyber-card-elevated text-center p-2 h-100"
              style={{ border: "1px solid rgba(245, 158, 11, 0.25)" }}
            >
              <FontAwesomeIcon
                icon={faBell}
                style={{ color: "var(--warning)", fontSize: "1rem", marginBottom: "4px" }}
              />
              <div style={{ fontWeight: 600, fontSize: "0.8rem" }}>Alerts</div>
              <div className="text-muted font-mono" style={{ fontSize: "0.68rem" }}>
                Triage Matrix
              </div>
            </div>
          </div>
          <div className="col-4">
            <div
              className="cyber-card-elevated text-center p-2 h-100"
              style={{ border: "1px solid rgba(34, 197, 94, 0.2)" }}
            >
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                style={{ color: "var(--success)", fontSize: "1rem", marginBottom: "4px" }}
              />
              <div style={{ fontWeight: 600, fontSize: "0.8rem" }}>Investigation</div>
              <div className="text-muted font-mono" style={{ fontSize: "0.68rem" }}>
                Log Forensics
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Model Note */}
      <div className="mt-3 pt-3 text-center" style={{ borderTop: "1px solid var(--border-subtle)" }}>
        <p className="font-mono text-muted mb-0" style={{ fontSize: "0.72rem" }}>
          Structural representation of centralized monitoring and triage architecture.
        </p>
      </div>
    </div>
  );
}

